import React, { useState, useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Slide,
    Divider, 
    Tooltip, 
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography
} from '@mui/material';

import api from '../../../api'
import { getSession } from '../../../auth'

import LoadingButton from '@mui/lab/LoadingButton';

import PropTypes from "prop-types";

import CloseIcon from '@mui/icons-material/Close';

import './styles.css'

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const MergeClientDialog = ({ open, clients, selectedClients, customFields, handleClose, handleSubmit }) => {

    const [client1, setClient1] = useState({})
    const [client2, setClient2] = useState({})
    const [finalClient, setFinalClient] = useState({})
    const [client1CustomFields, setClient1CustomFields] = useState({})
    const [client2CustomFields, setClient2CustomFields] = useState({})
    const [finalCustomFields, setFinalCustomFields] = useState({})
    const [client1Channels, setClient1Channels] = useState([])
    const [client2Channels, setClient2Channels] = useState([])
    const [selectedConflictChannels, setSelectedConflictChannels] = useState({})
    const [loadingChannels, setLoadingChannels] = useState(false)

    const renderKeys = {
        "name"                    : "Nome",
        "email"                   : "Email",
        "telephone"               : "Telefone",
        "contact_enterprise_name" : "Empresa",
        "cpf"                     : "CPF",
        "customer_code"           : "Código"
    }

    const prepareFinalClient = (client1, client2) => {
        const client = (client2.created_at < client1.created_at) ? client2 : client1
        const finalClient = {...client}
        Object.keys(finalClient).forEach((key) => {
            if (key === 'groups') {
                // Merge both clients groups
                finalClient[key] = [...client1['groups'], ...client2['groups']]
                return
            }
            if (! finalClient[key])
                finalClient[key] = client1[key] || client2[key]
        })
        setFinalClient(finalClient)
    }

    const getAllChannelGroups = (firstChannels, secondChannels) => {
        const grouped = {}

        firstChannels.forEach((channel) => {
            if (!grouped[channel.channel])
                grouped[channel.channel] = { first: [], second: [] }
            grouped[channel.channel].first.push(channel)
        })

        secondChannels.forEach((channel) => {
            if (!grouped[channel.channel])
                grouped[channel.channel] = { first: [], second: [] }
            grouped[channel.channel].second.push(channel)
        })

        return Object.keys(grouped)
            .sort((a, b) => a.localeCompare(b))
            .map((channelType) => ({
                channel: channelType,
                byContact: {
                    first: grouped[channelType].first,
                    second: grouped[channelType].second
                },
                isConflict: grouped[channelType].first.length > 0 && grouped[channelType].second.length > 0
            }))
    }

    const getDefaultSelectedConflicts = (firstClient, secondClient, conflictGroups) => {
        if (!firstClient || !secondClient)
            return {}

        const olderClientId = new Date(firstClient.created_at) <= new Date(secondClient.created_at)
            ? firstClient.id
            : secondClient.id

        const selectedByType = {}

        conflictGroups.forEach(({ channel, byContact }) => {
            const olderIsFirst = olderClientId === firstClient.id
            if (olderIsFirst && byContact.first.length > 0) {
                selectedByType[channel] = 'first'
                return
            }
            if (!olderIsFirst && byContact.second.length > 0) {
                selectedByType[channel] = 'second'
                return
            }
            selectedByType[channel] = byContact.first.length > 0 ? 'first' : 'second'
        })

        return selectedByType
    }

    const selectConflictChannel = (channelType, side) => {
        setSelectedConflictChannels((prev) => ({
            ...prev,
            [channelType]: side
        }))
    }

    useEffect(() => {
        const setupDialog = async () => {
            const selectedClient1 = clients.find((client) => client.id === selectedClients[0])
            const selectedClient2 = clients.find((client) => client.id === selectedClients[1])

            setClient1(selectedClient1 || {})
            setClient2(selectedClient2 || {})

            if (selectedClient1 && selectedClient2)
                prepareFinalClient(selectedClient1, selectedClient2)

            const customFieldsData1 = {}
            const customFieldsData2 = {}

            if (selectedClient1 && selectedClient1.custom_fields) {
                selectedClient1.custom_fields.forEach(field => {
                    const fieldId = typeof field.custom_field === 'object' ? field.custom_field.id : field.custom_field
                    customFieldsData1[fieldId] = field.value
                })
            }

            if (selectedClient2 && selectedClient2.custom_fields) {
                selectedClient2.custom_fields.forEach(field => {
                    const fieldId = typeof field.custom_field === 'object' ? field.custom_field.id : field.custom_field
                    customFieldsData2[fieldId] = field.value
                })
            }

            setClient1CustomFields(customFieldsData1)
            setClient2CustomFields(customFieldsData2)
            setFinalCustomFields(customFieldsData1)

            if (!selectedClient1?.id || !selectedClient2?.id) {
                setClient1Channels([])
                setClient2Channels([])
                setSelectedConflictChannels({})
                return
            }

            setLoadingChannels(true)
            const channels1Response = await api.get(`api/enterprises/${getSession().profile.enterprise.id}/contact_channels/${selectedClient1.id}/contact/`)
            const channels2Response = await api.get(`api/enterprises/${getSession().profile.enterprise.id}/contact_channels/${selectedClient2.id}/contact/`)
            setLoadingChannels(false)

            const channels1 = channels1Response.status === 200 ? channels1Response.data : []
            const channels2 = channels2Response.status === 200 ? channels2Response.data : []

            setClient1Channels(channels1)
            setClient2Channels(channels2)

            const conflictGroups = getAllChannelGroups(channels1, channels2).filter((each) => each.isConflict)
            const defaultSelection = getDefaultSelectedConflicts(selectedClient1, selectedClient2, conflictGroups)
            setSelectedConflictChannels(defaultSelection)
        }

        if (open)
            setupDialog()
    }, [open, clients, selectedClients])

    const checkSelected = (key, value, priority) => {
        if (value === finalClient[key] && client1[key] === client2[key])
            return priority
        return value === finalClient[key]
    }

    const emptyBox = () => {
        return (
            <Box style={{ height: "20px" }}></Box>
        )
    }

    const updateFinalClient = (key, value, extra=null) => {
        setFinalClient(prev => ({ ...prev, [key]: value }));
        if (key === "contact_enterprise_name")
            setFinalClient(prev => ({ ...prev, contact_enterprise: extra }))
    }

    useEffect(() => {}, [finalClient]);

    const channelGroups = getAllChannelGroups(client1Channels, client2Channels)
    const conflictChannelGroups = channelGroups.filter((each) => each.isConflict)

    return (
        <div>
            <Dialog
                open={open}
                keepMounted
                onClose={handleClose}
                aria-labelledby="alert-dialog-slide-title"
                aria-describedby="alert-dialog-slide-description"
                disableEnforceFocus
            >
                <DialogTitle id="alert-dialog-slide-title" className={'group-flexbox'}>
                    <span>Mesclar contatos</span>
                    <Tooltip title="Fechar" >
                        <Button className="action-button">
                            <CloseIcon onClick={handleClose} />
                        </Button>
                    </Tooltip>
                </DialogTitle>
                <Divider />
                <DialogContent>
                    A seguir, selecione o valor que deve ser priorizado no resultado final:
                    <Box className="merge-content">
                        <List>
                            {Object.keys(client1).map((key) => (
                                (key in renderKeys)
                                    ? <ListItem disablePadding key={`${key}-1`} className={checkSelected(key, client1[key], true) ? "item-selected" : ""}>
                                        <ListItemButton onClick={() => updateFinalClient(key, client1[key], client1["contact_enterprise"])}>
                                            <ListItemText 
                                                primary={renderKeys[key]} 
                                                secondary={Array.isArray(client1[key]) ? (client1[key][0] || emptyBox()) : (client1[key] || emptyBox())}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                    : null
                            ))}
                        </List>
                        <List>
                            {Object.keys(client2).map((key) => (
                                (key in renderKeys)
                                    ? <ListItem disablePadding key={`${key}-2`} className={checkSelected(key, client2[key], false) ? "item-selected" : ""}>
                                        <ListItemButton onClick={() => updateFinalClient(key, client2[key], client2["contact_enterprise"])}>
                                            <ListItemText 
                                                primary={renderKeys[key]} 
                                                secondary={Array.isArray(client2[key]) ? (client2[key][0] || emptyBox()) : (client2[key] || emptyBox())}
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                    : null
                            ))}
                        </List>
                    </Box>
                    
                    {customFields && customFields.length > 0 && (
                        <>
                            <Typography marginTop="2rem" marginBottom="1rem" fontSize="17px" fontWeight="500" color="#21446c">
                                Campos Customizados
                            </Typography>
                            <Box className="merge-content">
                                <List>
                                    {customFields.map((field) => {
                                        const value1 = client1CustomFields[field.id] || ''
                                        const isSelected1 = finalCustomFields[field.id] === value1 || (!finalCustomFields[field.id] && !value1)
                                        return (
                                            <ListItem disablePadding key={`cf-${field.id}-1`} className={isSelected1 ? "item-selected" : ""}>
                                                <ListItemButton onClick={() => setFinalCustomFields(prev => ({ ...prev, [field.id]: value1 }))}>
                                                    <ListItemText 
                                                        primary={field.field_name}
                                                        secondary={value1 || emptyBox()}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        )
                                    })}
                                </List>
                                <List>
                                    {customFields.map((field) => {
                                        const value2 = client2CustomFields[field.id] || ''
                                        const isSelected2 = finalCustomFields[field.id] === value2 || (!finalCustomFields[field.id] && !value2)
                                        return (
                                            <ListItem disablePadding key={`cf-${field.id}-2`} className={isSelected2 ? "item-selected" : ""}>
                                                <ListItemButton onClick={() => setFinalCustomFields(prev => ({ ...prev, [field.id]: value2 }))}>
                                                    <ListItemText 
                                                        primary={field.field_name}
                                                        secondary={value2 || emptyBox()}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        )
                                    })}
                                </List>
                            </Box>
                        </>
                    )}

                    <Typography marginTop="2rem" marginBottom="1rem" fontSize="17px" fontWeight="500" color="#21446c">
                        Canais
                    </Typography>
                    {loadingChannels
                        ? <Typography variant="body2">Carregando canais...</Typography>
                        : <>
                            {conflictChannelGroups.length > 0 &&
                                <Typography variant="body2" marginBottom="0.5rem">
                                    Selecione qual canal manter para cada tipo duplicado.
                                </Typography>
                            }
                            {channelGroups.length === 0
                                ? <Typography variant="body2">Sem canais para exibir.</Typography>
                                : <>
                                    {conflictChannelGroups.length === 0 &&
                                        <Typography variant="body2" marginBottom="0.5rem">Sem conflitos de canais. A mesclagem será automática.</Typography>
                                    }
                                    <Box className="merge-content">
                                    <List>
                                        {channelGroups.map(({ channel, byContact, isConflict }) => {
                                            const selected = isConflict
                                                ? selectedConflictChannels[channel] === 'first'
                                                : byContact.first.length > 0
                                            const nicks = byContact.first.map((item) => item.nick).join(', ')
                                            return (
                                                <ListItem disablePadding key={`conflict-first-${channel}`} className={selected ? "item-selected" : ""}>
                                                    <ListItemButton onClick={isConflict ? () => selectConflictChannel(channel, 'first') : undefined} disabled={!isConflict}>
                                                        <ListItemText
                                                            primary={channel}
                                                            secondary={nicks || emptyBox()}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            )
                                        })}
                                    </List>
                                    <List>
                                        {channelGroups.map(({ channel, byContact, isConflict }) => {
                                            const selected = isConflict
                                                ? selectedConflictChannels[channel] === 'second'
                                                : byContact.second.length > 0
                                            const nicks = byContact.second.map((item) => item.nick).join(', ')
                                            return (
                                                <ListItem disablePadding key={`conflict-second-${channel}`} className={selected ? "item-selected" : ""}>
                                                    <ListItemButton onClick={isConflict ? () => selectConflictChannel(channel, 'second') : undefined} disabled={!isConflict}>
                                                        <ListItemText
                                                            primary={channel}
                                                            secondary={nicks || emptyBox()}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            )
                                        })}
                                    </List>
                                </Box>
                                </>
                            }
                        </>
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Cancelar
                    </Button>
                    <LoadingButton
                        onClick={() => {
                            const selectedIds = conflictChannelGroups
                                .flatMap(({ channel, byContact }) => {
                                    const side = selectedConflictChannels[channel]
                                    if (side === 'first')
                                        return byContact.first.map((item) => item.id)
                                    if (side === 'second')
                                        return byContact.second.map((item) => item.id)
                                    return []
                                })
                            handleSubmit(client1, client2, finalClient, finalCustomFields, selectedIds)
                        }}
                        color="primary"
                    >
                        Enviar
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </div>
    );
}

MergeClientDialog.propTypes = {
    open: PropTypes.bool,
    selectedClients: PropTypes.array,
    customFields: PropTypes.array,
    handleClose: PropTypes.func,
    handleSubmit: PropTypes.func
};

export default MergeClientDialog;
